import { envConfig } from "@configs/env";
import { APP_VERSION_FORMAT } from "@constants/ui";
import logger from "@utils/logger";
import moment from "moment";

export const formatTimer = (
  timeout: number,
  leadingZero = {
    minutes: true,
    seconds: true,
  },
) => {
  const minutes = Math.floor(timeout / 60);
  const seconds = timeout % 60;

  let formattedMinutes = minutes.toString();
  let formattedSeconds = seconds.toString();

  if (leadingZero.minutes && minutes < 10) {
    formattedMinutes = `0${minutes}`;
  }

  if (leadingZero.seconds && seconds < 10) {
    formattedSeconds = `0${seconds}`;
  }

  //ignore minutes if it's 0
  if (minutes === 0 && !leadingZero.minutes) {
    return formattedSeconds;
  }

  return `${formattedMinutes}:${formattedSeconds}`;
};

export const getBrandResource = (path: string) => {
  // remove leading slash
  if (path.startsWith("/")) {
    path = path.slice(1);
  }
  return `/brands/${envConfig.brand.key}/${path}`;
};

export const randomInt = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

/**
 * Standardize domain
 * Remove protocol prefix
 * Remove trailing slash
 * Remove www prefix
 * @param domain
 * @returns
 */
export const standardizeDomain = (domain: string) => {
  return domain
    .replace(/^https?:\/\//, "")
    .replace(/\/$/, "")
    .replace(/^www\./, "");
};

/**
 * Safe parse json, return null if failed
 * @param json
 * @returns
 */
export const safeParseJson = <T = any>(json: string): T | null => {
  try {
    return JSON.parse(json);
  } catch (error) {
    return null;
  }
};

/**
 * Check if the source is a video file
 * @param src
 * @returns
 */
export const isVideoFile = (src: string): boolean => {
  const videoExtensions = [".webm", ".mp4"];
  if (!src) return false;

  try {
    const url = new URL(src);
    const pathname = url.pathname.toLowerCase().replace(/\/$/, "");
    return videoExtensions.some((extension) => pathname.endsWith(extension));
  } catch (error) {
    // fall back to the original method for relative paths
    return videoExtensions.some((extension) => src.toLowerCase().endsWith(extension));
  }
};

/**
 * Get version number of the application
 * @param customFormat Optional custom format for version
 * @returns Application version string
 */
export const getVersionNumber = (customFormat = APP_VERSION_FORMAT): string => {
  try {
    const version = import.meta.env.VITE_APP_VERSION || moment().format(customFormat);
    return version ? version.substring(0, 8) : version;
  } catch (error) {
    logger._console.log("Error getting version number");
    return moment().format(customFormat).substring(0, 8);
  }
};

/**
 * Mask sensitive data with a given mask pattern
 * Nested objects and arrays are supported
 */
export const maskSensitiveData = (data: any, sensitiveFields: string[], maskPattern = "******"): any => {
  if (!data) {
    return undefined;
  }

  if (Array.isArray(data)) {
    return data.map((item) => maskSensitiveData(item, sensitiveFields, maskPattern));
  }

  if (typeof data !== "object" || data === null) {
    return data;
  }

  const maskedData = { ...data };

  Object.keys(maskedData).forEach((key) => {
    if (sensitiveFields.includes(key)) {
      maskedData[key] = maskPattern;
    } else {
      maskedData[key] = maskSensitiveData(maskedData[key], sensitiveFields, maskPattern);
    }
  });

  return maskedData;
};
